import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  OutlinedInput,
  ListItemText,
  Checkbox,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import SnackbarMessage from '../SnackbarMessage'
import { useAuth } from '../../context/AuthContext'
import supabase from '../../supabaseClient'

function EditTeacherForm({ teacher, open, onClose, onSuccess }) {
  const { userRole } = useAuth()
  const isAdmin = userRole === 'admin' || userRole === 'super_admin'
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    gender: '',
    class_ids: [],
  })
  const [classes, setClasses] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [phoneError, setPhoneError] = useState('')
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  })

  // Fetch all available classes and the teacher's assigned classes
  useEffect(() => {
    const fetchClasses = async () => {
      if (!open) return

      // Fetch all classes
      const { data: allClasses, error: classesError } = await supabase
        .from('classes')
        .select('id, name, teacher_id')
        .order('name')

      if (!classesError && allClasses) {
        setClasses(allClasses)
        
        // If we have a teacher, set their assigned classes
        if (teacher?.id) {
          const assignedClassIds = allClasses
            .filter(c => c.teacher_id === teacher.id)
            .map(c => c.id)
          setFormData(prev => ({ ...prev, class_ids: assignedClassIds }))
        }
      }
    }

    fetchClasses()
  }, [open, teacher?.id, teacher])

  // Reset form when dialog opens with teacher data
  useEffect(() => {
    if (open && teacher) {
      setFormData({
        first_name: teacher.first_name || '',
        last_name: teacher.last_name || '',
        email: teacher.email || '',
        phone: teacher.phone || '',
        gender: teacher.gender || '',
        class_ids: [], // This will be set by the classes fetch effect
      })
    }
  }, [teacher, open])

  // If not admin, don't render anything
  if (!isAdmin) return null

  const handleChange = (e) => {
    const { name, value } = e.target
    if (name === 'phone') {
      // Only allow digits, format as phone number
      const formatted = formatPhone(value)
      setFormData((prev) => ({ ...prev, phone: formatted }))
      // Validate length
      const digits = value.replace(/\D/g, '')
      if (digits.length > 0 && digits.length !== 10) {
        setPhoneError('Phone number must be 10 digits')
      } else {
        setPhoneError('')
      }
    } else if (name === 'first_name' || name === 'last_name') {
      // Only allow letters and spaces
      const letterOnlyValue = value.replace(/[^A-Za-z\s]/g, '')
      setFormData((prev) => ({
        ...prev,
        [name]: letterOnlyValue,
      }))
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }))
    }
  }

  const handleClassChange = (e) => {
    const { value } = e.target
    setFormData((prev) => ({
      ...prev,
      class_ids: typeof value === 'string' ? value.split(',') : value,
    }))
  }

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false })
  }

  const handleClose = () => {
    // Reset form data to original teacher data
    if (teacher) {
      setFormData({
        first_name: teacher.first_name || '',
        last_name: teacher.last_name || '',
        email: teacher.email || '',
        phone: teacher.phone || '',
        gender: teacher.gender || '',
        class_ids: [], // This will be reset by the classes fetch effect
      })
    }
    setError('')
    setPhoneError('')
    onClose()
  }

  const hasValidationErrors = () => {
    // Check for empty required fields
    if (!formData.first_name.trim() || !formData.last_name.trim() || !formData.email.trim()) {
      return true
    }

    // Check for valid email format
    if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      return true
    }

    // Check for gender
    if (!formData.gender) {
      return true
    }

    // Check for phone error if phone is provided
    if (formData.phone && phoneError) {
      return true
    }

    return false
  }

  const validateForm = () => {
    if (!formData.first_name.trim()) {
      setError('First name is required')
      return false
    }
    if (!formData.last_name.trim()) {
      setError('Last name is required')
      return false
    }
    if (!formData.email.trim()) {
      setError('Email is required')
      return false
    }
    if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setError('Please enter a valid email address')
      return false
    }
    if (!formData.gender) {
      setError('Gender is required')
      return false
    }
    if (formData.phone) {
      const digits = formData.phone.replace(/\D/g, '')
      if (digits.length !== 10) {
        setError('Phone number must be 10 digits')
        return false
      }
    }
    return true
  }


  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    if (!validateForm()) {
      setLoading(false)
      return
    }

    try {
      // First update the teacher's information
      const { error: updateError } = await supabase
        .from('teachers')
        .update({
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          email: formData.email.trim(),
          phone: formData.phone ? formData.phone.replace(/\D/g, '') : null,
          gender: formData.gender,
          updated_at: new Date().toISOString(),
        })
        .eq('id', teacher.id)

      if (updateError) throw updateError

      // Then update the class assignments
      // First, clear all class assignments for this teacher
      const { error: clearClassesError } = await supabase
        .from('classes')
        .update({ teacher_id: null })
        .eq('teacher_id', teacher.id)

      if (clearClassesError) throw clearClassesError

      // Then assign the selected classes to this teacher
      if (formData.class_ids.length > 0) {
        const { error: assignClassError } = await supabase
          .from('classes')
          .update({ teacher_id: teacher.id })
          .in('id', formData.class_ids)

        if (assignClassError) throw assignClassError
      }

      setSnackbar({
        open: true,
        message: 'Teacher information updated successfully!',
        severity: 'success',
      })

      if (onSuccess) onSuccess()
      handleClose()
    } catch (error) {
      console.error('Error updating teacher:', error)
      setSnackbar({
        open: true,
        message: 'Failed to update teacher information. Please try again.',
        severity: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  // Helper function to format phone numbers
  const formatPhone = (value) => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '')
    // Format as (XXX) XXX-XXXX
    if (digits.length <= 3) return digits
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`
  }

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            maxHeight: { xs: '100vh', sm: '90vh' },
            margin: { xs: 0, sm: 2 },
            borderRadius: { xs: 0, sm: 2 },
          },
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Edit Teacher Information
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2} sx={{ mt: { xs: 0.5, sm: 1 } }}>
              {error && (
                <Grid item xs={12}>
                  <Alert severity="error" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, '& .MuiAlert-message': { fontSize: { xs: '0.875rem', sm: '1rem' } } }}>
                    {error}
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  error={!formData.first_name.trim()}
                  helperText={!formData.first_name.trim() ? 'First name is required' : ''}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  error={!formData.last_name.trim()}
                  helperText={!formData.last_name.trim() ? 'Last name is required' : ''}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  error={!formData.email.trim() || !formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)}
                  helperText={
                    !formData.email.trim()
                      ? 'Email is required'
                      : !formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)
                      ? 'Please enter a valid email address'
                      : ''
                  }
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  error={!!phoneError}
                  helperText={phoneError}
                  placeholder="(555) 555-5555"
                  inputProps={{ maxLength: 14 }}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth required error={!formData.gender}>
                  <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Gender</InputLabel>
                  <Select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    label="Gender"
                    sx={{
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      '& .MuiSelect-select': {
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                      },
                    }}
                  >
                    <MenuItem value="Male" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Male</MenuItem>
                    <MenuItem value="Female" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Female</MenuItem>
                  </Select>
                  {!formData.gender && (
                    <FormHelperText sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Gender is required</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Class Assignments</InputLabel>
                  <Select
                    multiple
                    name="class_ids"
                    value={formData.class_ids}
                    onChange={handleClassChange}
                    input={<OutlinedInput label="Class Assignments" />}
                    renderValue={(selected) =>
                      classes
                        .filter((c) => selected.includes(c.id))
                        .map((c) => c.name)
                        .join(', ')
                    }
                    sx={{
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      '& .MuiSelect-select': {
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                      },
                    }}
                  >
                    {classes.map((c) => (
                      <MenuItem key={c.id} value={c.id} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        <Checkbox checked={formData.class_ids.indexOf(c.id) > -1} size="small" />
                        <ListItemText primary={c.name} primaryTypographyProps={{ fontSize: { xs: '0.875rem', sm: '1rem' } }} />
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Optional: Select classes taught by this teacher</FormHelperText>
                </FormControl>
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions sx={{ 
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 1 },
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          gap: { xs: 1, sm: 0 },
        }}>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', width: { xs: '100%', sm: 'auto' }, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Button 
              onClick={handleClose}
              fullWidth={isMobile}
              sx={{ 
                fontSize: { xs: '0.875rem', sm: '1rem' },
                padding: { xs: '10px 16px', sm: '8px 16px' },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              disabled={loading || hasValidationErrors()}
              fullWidth={isMobile}
              sx={{ 
                fontSize: { xs: '0.875rem', sm: '1rem' },
                padding: { xs: '10px 16px', sm: '8px 16px' },
              }}
            >
              {loading ? <CircularProgress size={isMobile ? 20 : 24} /> : 'Save Changes'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <SnackbarMessage
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
      />
    </>
  )
}

export default EditTeacherForm 